// ============================================
// MiRutina — rutina por producto (/mi-rutina).
// La clienta elige lo que tiene (o llega desde un pedido con
// ?productos=handle1,handle2) y el motor del backend arma su rutina.
// Solo productos sueltos: los kits no entran en la rutina.
// ============================================

import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import RutinaResultado from '../components/rutina/RutinaResultado';
import { useProducts } from '../hooks/useProducts';
import { crearRutina, getProductosRutina } from 'services/medusa/rutina';
import './Diagnostico.css';
import './MiRutina.css';

const PIELES = [
  ['grasa', 'Grasa'],
  ['mixta', 'Mixta'],
  ['seca', 'Seca'],
  ['normal', 'Normal'],
];

export default function MiRutina() {
  const { allProducts } = useProducts();
  const [params, setParams] = useSearchParams();
  const [handles, setHandles] = useState([]);
  const [seleccion, setSeleccion] = useState(() =>
    (params.get('productos') || '').split(',').filter(Boolean)
  );
  const [perfil, setPerfil] = useState({ piel: '', sensible: false, embarazo: false });
  const [resultado, setResultado] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getProductosRutina().then(setHandles).catch(() => setError('No pudimos cargar los productos.'));
  }, []);

  const productos = useMemo(() => {
    const set = new Set(handles);
    return allProducts.filter((p) => set.has(p.handle)).sort((a, b) => a.name.localeCompare(b.name));
  }, [allProducts, handles]);

  const armar = async (lista = seleccion) => {
    const validos = lista.filter((h) => handles.includes(h));
    if (!validos.length) return;
    setCargando(true);
    setError('');
    try {
      // Se mandan también los kits para que el motor avise que no entran.
      const res = await crearRutina({
        productos: lista,
        perfil: {
          piel: perfil.piel || undefined,
          sensible: perfil.sensible,
          embarazo: perfil.embarazo ? 'si' : 'no',
        },
      });
      setResultado(res);
      setParams({ productos: validos.join(',') }, { replace: true });
      window.scrollTo({ top: 0 });
    } catch (e) {
      setError(e.message || 'No pudimos armar tu rutina. Intenta de nuevo.');
    } finally {
      setCargando(false);
    }
  };

  // Desde un pedido (?productos=...) la rutina se arma sola al cargar.
  useEffect(() => {
    if (handles.length && params.get('productos') && !resultado) armar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handles]);

  const elegidos = seleccion.filter((h) => handles.includes(h));

  const alternar = (h) =>
    setSeleccion((s) => (s.includes(h) ? s.filter((x) => x !== h) : [...s, h]));

  const seo = (
    <SEO
      title="Arma tu rutina"
      description="Elige tus productos Physiology y te decimos cómo usarlos: en qué orden, de mañana o de noche y qué días."
      path="/mi-rutina"
    />
  );

  if (resultado) {
    const n = elegidos.length;
    return (
      <main className="dx dx--result">
        {seo}
        <aside className="dx-summary">
          <p className="dx-eyebrow">Tu rutina</p>
          <h1 className="dx-title">Así se usan tus productos</h1>
          <p className="dx-text">
            Ordenamos {n === 1 ? 'tu producto' : `tus ${n} productos`} por paso, momento del día y días de la semana.
          </p>
          <button className="dx-link" onClick={() => setResultado(null)}>Cambiar productos</button>
        </aside>
        <section className="dx-routine">
          <RutinaResultado rutina={resultado} productos={allProducts} />
        </section>
      </main>
    );
  }

  return (
    <main className="dx">
      {seo}
      <div className="mr">
        <p className="dx-eyebrow">Tu rutina</p>
        <h1 className="dx-title">Arma tu rutina con tus productos</h1>
        <p className="dx-text">
          Elige los productos que tienes y te decimos cómo usarlos: en qué orden, de mañana o de noche y qué días.
        </p>

        <div className="mr-grid">
          {productos.map((p) => {
            const activo = seleccion.includes(p.handle);
            return (
              <button
                key={p.handle}
                className={`mr-item ${activo ? 'is-active' : ''}`}
                aria-pressed={activo}
                onClick={() => alternar(p.handle)}
              >
                <span className="mr-item__img">{p.image && <img src={p.image} alt="" loading="lazy" />}</span>
                <span className="mr-item__name">{p.name}</span>
              </button>
            );
          })}
        </div>

        <div className="mr-perfil">
          <p className="dx-eyebrow">Opcional: cuéntanos de tu piel</p>
          <div className="mr-chips">
            {PIELES.map(([id, label]) => (
              <button
                key={id}
                className={`mr-chip ${perfil.piel === id ? 'is-active' : ''}`}
                aria-pressed={perfil.piel === id}
                onClick={() => setPerfil({ ...perfil, piel: perfil.piel === id ? '' : id })}
              >
                {label}
              </button>
            ))}
          </div>
          <label className="mr-check">
            <input type="checkbox" checked={perfil.sensible} onChange={(e) => setPerfil({ ...perfil, sensible: e.target.checked })} />
            Mi piel es sensible
          </label>
          <label className="mr-check">
            <input type="checkbox" checked={perfil.embarazo} onChange={(e) => setPerfil({ ...perfil, embarazo: e.target.checked })} />
            Estoy embarazada o en lactancia
          </label>
        </div>

        {error && <p className="dx-error">{error}</p>}

        <div className="mr-bar">
          <span className="dx-meta">
            {elegidos.length ? `${elegidos.length} seleccionado${elegidos.length > 1 ? 's' : ''}` : 'Elige al menos un producto'}
          </span>
          <button className="dx-btn" disabled={!elegidos.length || cargando} onClick={() => armar()}>
            {cargando ? 'Armando…' : 'Armar mi rutina'}
          </button>
        </div>
      </div>
    </main>
  );
}
